import { dateTag } from '@croissants/i18n';
import { Modal, Spinner, usePlatformNow } from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useShopFacts, useWindows } from '../hooks/queries';
import { isShopChanged } from '../lib/api';
import {
  earliestArrivalMinutes,
  type NextOpening,
  nextOpening,
  orderNowMinutesOf,
} from '../lib/delivery-choice';
import { pickerThen } from '../lib/journey';
import { weeklyHoursPhrases } from '../lib/shop-facts';
import { soonestMorning } from '../lib/stay';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';
import { AsyncPanel, ErrorNotice } from './feedback';
import { ShopChangedRedirect } from './ShopChangedRedirect';

/**
 * MODE B2's DELIVERY POPUP (founder decision, 2026-09-21; PRD GST-045).
 *
 * A B2 scan opens the shelf, and the delivery time is asked HERE, over it -
 * replacing the two-section first screen of the day before, which stood between
 * the scan and the products. It is raised two ways:
 *
 *   - by the guest, from the basket bar and the basket's own button, while the
 *     closest bakery is OPEN: two sections, "Order now" with the earliest
 *     arrival, and "Schedule for later" with the week the bakery keeps;
 *   - by the scan itself, while that bakery is SHUT: one sentence saying so and
 *     when it opens again, and the one thing that can still be done - schedule.
 *
 * The sections are the two products today sells (GST-042, RTE-034), each behind
 * the switch that sells it, exactly as the screen it replaces had them - a
 * switch that is off is a section that is absent.
 *
 * NOBODY IS NAMED (CAT-009, GST-030): every fact here comes off
 * `GET /g/shop-facts`, which carries no id, name, logo or description, and off
 * the picker's own answer.
 *
 * It can always be closed, shut bakery included. Behind it sit the Orders tab
 * and the support link, and a guest with a bag already on its way who scans
 * again at night must be able to reach both.
 *
 * Open, it is ALWAYS drawn: the facts read sits behind the shared panel, so a
 * press on the basket bar over a slow or failed read opens a popup that says
 * so and offers the retry, never a button that did nothing.
 */
export function DeliveryChoice({
  open,
  onClose,
  next,
}: {
  open: boolean;
  onClose: () => void;
  /** Where the guest goes once a time is settled: the basket, or back to the shelf. */
  next: string;
}): JSX.Element | null {
  const { locale, t } = useLocale();
  const { session } = useSession();
  const { basket } = useBasket();
  const { update } = useCheckoutDraft();
  const [, navigate] = useLocation();

  const modes = session?.orderingModes ?? null;
  const facts = useShopFacts(open);
  /* The picker's own answer, under the key the picker and the shelf already
     ask under: the estimate below and the window "Order now" settles are read
     off a cache, and are the ones the picker would have offered. */
  const windows = useWindows(basket, null, open);
  const platformNow = usePlatformNow(windows.data?.serverTime ?? null, 60_000);

  if (!open) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('delivery.title')}
      /* No drawn title (founder, 2026-09-21): the two sections say what the
         popup is for. It is still NAMED - the heading is hidden, not dropped. */
      titleHidden
      closeLabel={t('delivery.close')}
      size="sm"
      className="gw-deliverychoice"
    >
      <AsyncPanel
        query={facts}
        error={(failure, retry) =>
          /* The bound shop stopped trading mid-visit: the same recovery the
             shelf and the picker make (founder, 2026-09-20). */
          isShopChanged(failure) ? (
            <ShopChangedRedirect />
          ) : (
            <ErrorNotice error={failure} onRetry={retry} />
          )
        }
      >
        {(answer) => {
          const shop = answer.facts;
          const nowOffered = shop.orderableNow && modes?.immediate === true;
          const minutes = nowOffered
            ? orderNowMinutesOf(shop, () =>
                earliestArrivalMinutes(windows.data?.serviceDays, platformNow),
              )
            : null;
          const week = weeklyHoursPhrases(shop.weeklyHours, locale, {
            everyDay: t('shops.everyDay'),
            closed: (days) => t('shops.closedOn', { days }),
          });
          /* "Your baker is open" and then the week, phrase by phrase, as the
             chooser's tile prints it (GST-031) - the same helper, so the two
             cannot disagree. */
          const openHours = week.length === 0 ? [] : [t('delivery.bakerOpen'), ...week];

          const orderNow = (): void => {
            /*
             * Settle today's soonest and move on. If the picker's answer has
             * not landed - a slow first paint, a dropped connection - the guest
             * goes through the picker instead of nowhere: it opens on that same
             * row (RTE-034).
             */
            const morning = soonestMorning(windows.data?.serviceDays);
            onClose();
            if (morning === null) {
              navigate(pickerThen(next));
              return;
            }
            update({ ...morning, selectedDays: [...morning.selectedDays] });
            navigate(next);
          };

          const schedule = (): void => {
            onClose();
            /* Declined one tap ago, so the picker does not offer Soonest again. */
            navigate(pickerThen(next, { later: nowOffered }));
          };

          return (
            <>
              {/* SHUT is a statement about an order for now, so it is made only
                  while one is sold: with immediate delivery off, "order now is
                  not on offer" says nothing about the door
                  (`deliveryChoiceAsked`). */}
              {nowOffered || modes?.immediate !== true ? null : (
                /* The shut bakery, said the way a guest has read it elsewhere
                   (founder, 2026-09-21): one sentence standing as the heading,
                   when it opens again under it, and the one thing left to do. */
                <div className="gw-deliverychoice__closed">
                  <p className="gw-deliverychoice__closedtitle">{t('delivery.closed')}</p>
                  {opensLine(nextOpening(shop, platformNow), locale, t) === null ? null : (
                    <p className="gw-deliverychoice__opens">
                      {opensLine(nextOpening(shop, platformNow), locale, t)}
                    </p>
                  )}
                </div>
              )}
              <div className="gw-deliverychoice__choices">
                {nowOffered ? (
                  <Choice
                    label={t('delivery.now')}
                    onPress={orderNow}
                    /* THE LINE ARRIVES WITH THE POPUP (founder, 2026-09-21). The
                       figure comes off the picker's answer, which can land after
                       the shop facts that opened this popup; the words are drawn
                       at once with a loading wheel where the minutes will be, so
                       the section neither grows under the thumb nor looks, for a
                       second, like an order with no promise at all. */
                    lines={
                      minutes !== null
                        ? [t('delivery.inMinutes', { minutes })]
                        : shop.orderNowMinutes === undefined &&
                            windows.data === undefined &&
                            windows.error === null
                          ? [
                              <span key="loading" className="gw-starttile__loading">
                                {t('delivery.in')}
                                <Spinner size={14} />
                              </span>,
                            ]
                          : []
                    }
                  />
                ) : null}
                {modes?.preorder === true ? (
                  <Choice
                    /* One name for it, open or shut: the guest schedules for later. */
                    label={t('delivery.later')}
                    onPress={schedule}
                    lines={openHours}
                  />
                ) : null}
              </div>
            </>
          );
        }}
      </AsyncPanel>
    </Modal>
  );
}

/** "Opens tomorrow at 07:00", under the closed sentence - or nothing to state. */
function opensLine(
  opening: NextOpening | null,
  locale: 'en' | 'fr',
  t: ReturnType<typeof useLocale>['t'],
): string | null {
  if (opening === null) return null;
  if (opening.kind === 'TODAY') return t('delivery.opensToday', { time: opening.time });
  if (opening.kind === 'TOMORROW') return t('delivery.opensTomorrow', { time: opening.time });
  // Midday UTC keeps the calendar date stable, as `serviceDayLabel` does.
  const day = new Intl.DateTimeFormat(dateTag(locale), {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${opening.serviceDay}T12:00:00.000Z`));
  return t('delivery.opensLater', { day, time: opening.time });
}

/**
 * One section, and the WHOLE section is the control: a native `<button>`, so
 * Enter, Space and the tab order come with it.
 */
function Choice({
  label,
  onPress,
  lines,
}: {
  label: string;
  onPress: () => void;
  lines: readonly ReactNode[];
}): JSX.Element {
  return (
    <button type="button" className="gw-starttile" onClick={onPress}>
      <span className="gw-starttile__label">{label}</span>
      {lines.length === 0 ? null : (
        <span className="gw-starttile__facts">
          {lines.map((line, index) => (
            // A fixed, short list that is never reordered: its position is its key.
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            <span key={index} className="gw-starttile__fact">
              {line}
            </span>
          ))}
        </span>
      )}
    </button>
  );
}
